import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { composeClientLiner, loadClientLinerLayers, preloadLinerSwatches } from '../lib/clientLinerCanvas';

const API = process.env.REACT_APP_BACKEND_URL;

export const ClientLinerPreview = forwardRef(function ClientLinerPreview({
  sourceLookId, sourceImage, sourcePath, design, savedLiners, onReady,
}, ref) {
  const canvas = useRef(null), latest = useRef(null);
  const [preview, setPreview] = useState(null), [layers, setLayers] = useState(null);
  const [error, setError] = useState(''), [retry, setRetry] = useState(0);
  const [painting, setPainting] = useState(false);
  const key = JSON.stringify([sourceLookId, preview?.id, design.liner_id, design.liner_custom_url, design.panel_mode]);

  useEffect(() => { preloadLinerSwatches(savedLiners); }, [savedLiners]);
  useEffect(() => {
    let live = true;
    setPreview(null); setLayers(null); setError(''); onReady(false);
    (async () => {
      try {
        const { data } = await axios.post(`${API}/api/liner-preview/prepare`, {
          source_look_id: sourceLookId,
          source_image_path: sourcePath || null,
        }, { withCredentials: true, timeout: 150000 });
        const loaded = await loadClientLinerLayers(data);
        if (live) { setPreview(data); setLayers(loaded); }
      } catch (e) {
        if (live) setError(e?.response?.data?.detail || e.message || 'Could not prepare this jacket photo');
      }
    })();
    return () => { live = false; };
  }, [sourceLookId, sourcePath, retry, onReady]);

  useEffect(() => {
    if (!layers) return;
    let live = true;
    const startedAt = performance.now();
    onReady(false); setPainting(true); setError('');
    composeClientLiner(layers, design).then(image => {
      if (!live || !canvas.current) return;
      canvas.current.width = image.width; canvas.current.height = image.height;
      canvas.current.getContext('2d').putImageData(image, 0, 0);
      canvas.current.dataset.paintDurationMs = (performance.now() - startedAt).toFixed(1);
      canvas.current.dataset.linerId = design.liner_id;
      latest.current = key;
      setPainting(false); onReady(true);
    }).catch(e => { if (live) { setError(e.message); setPainting(false); } });
    return () => { live = false; };
  }, [layers, key, design, onReady]);

  useImperativeHandle(ref, () => ({
    getComposite: () => {
      if (!preview || latest.current !== key || error || painting) throw new Error('Wait for the selected liner preview');
      return { preview_id: preview.id, composite: canvas.current.toDataURL('image/png').split(',')[1] };
    },
  }), [preview, key, error, painting]);

  const src = sourceImage && (/^(data:|https?:|\/api\/)/.test(sourceImage) ? sourceImage : `data:image/jpeg;base64,${sourceImage}`);
  return (
    <div className="relative w-full h-full flex items-center justify-center bg-[#E5E5E5]" data-testid="client-liner-preview">
      {!layers && src && <img src={src} alt="Selected client look" className="w-full h-full object-contain" data-testid="liner-source-photo" />}
      <canvas ref={canvas} className={`w-full h-full object-contain ${layers ? '' : 'hidden'}`} aria-label="Client wearing the selected jacket and liner" data-testid="client-liner-canvas" />
      {!layers && !error && <div className="absolute bottom-5 inset-x-4 bg-black/85 text-white p-3 flex items-center justify-center gap-2" role="status" data-testid="liner-preparing-status">
        <Loader2 className="w-4 h-4 animate-spin" /><span className="text-xs">Preparing your open-jacket photo…</span>
      </div>}
      {layers && painting && <span className="absolute bottom-4 right-4 bg-black/80 text-white p-2 text-xs" role="status" data-testid="liner-loading-swatch">Loading liner…</span>}
      {error && <div className="absolute bottom-5 inset-x-4 bg-[#121215] text-white p-4 text-sm" role="alert" data-testid="liner-preview-error">
        <p>{error}</p>
        <button onClick={() => setRetry(n => n + 1)} className="mt-3 flex items-center gap-2 text-[#C2A878] hover:text-white transition-colors" data-testid="liner-preview-retry"><RefreshCw className="w-4 h-4" />Retry photo</button>
      </div>}
    </div>
  );
});